import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Loader2, ArrowRight, Trophy, AlertTriangle } from 'lucide-react';
import { useT } from '../i18n';
import { getRef } from '../lib/referral';
import { trackEvent } from '../lib/analytics';
import { HumanCheck } from './HumanCheck';

const API = process.env.REACT_APP_BACKEND_URL;

const Bar = ({ label, mine, rival, max }) => {
  const winning = mine >= rival;
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between text-[12px] mb-1.5">
        <span className="font-semibold text-neutral-700">{label}</span>
        <span className={`font-bold ${winning ? 'text-emerald-600' : 'text-red-500'}`}>
          {mine} vs {rival} <span className="text-neutral-400 font-medium">/ {max}</span>
        </span>
      </div>
      <div className="flex gap-1.5">
        <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full rounded-full bg-[#F77F11] transition-all duration-700" style={{ width: `${(mine / max) * 100}%` }} />
        </div>
        <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full rounded-full bg-neutral-400 transition-all duration-700" style={{ width: `${(rival / max) * 100}%` }} />
        </div>
      </div>
    </div>
  );
};

export default function CompetitorCompare() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [you, setYou] = useState('');
  const [rival, setRival] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState(null);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadErr, setLeadErr] = useState('');
  const [planHuman, setPlanHuman] = useState('');
  const [comparesToday, setComparesToday] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/index-ai/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.compares_today) setComparesToday(d.compares_today); })
      .catch(() => {});
  }, []);

  const c = isEs ? {
    eyebrow: 'Nuevo · Datos reales en vivo', h: '¿Quién está ganando online: ¿tú o tu competencia?',
    sub: 'Escaneamos tu web y la de tu competidor directo en vivo — misma tecnología, mismos 30+ chequeos — y te decimos exactamente quién domina Google, ChatGPT y Gemini. Sin opiniones: datos.',
    you: 'turestaurante.com', rival: 'tucompetidor.com', cta: 'Comparar gratis', scanning: 'Escaneando ambas webs en vivo…',
    win: '¡Vas ganando! Pero tu competencia está a un clic de alcanzarte.', lose: 'Tu competencia te está ganando online — cada día que pasa son clientes que van a su mesa, no a la tuya.',
    tie: 'Empate técnico — el primero que actúe se lleva la delantera.',
    yourSite: 'Tu restaurante', rivalSite: 'Competidor', ctaFull: 'Quiero mi informe completo y el plan para adelantarles',
    youLabel: 'Tu web', rivalLabel: 'La web de tu competidor',
    err: 'No pudimos escanear una de las webs. Revisa las URLs.',
    planLoseT: '🥊 Te enviamos GRATIS el plan de 90 días para adelantarles', planWinT: '🛡️ Te enviamos GRATIS el plan para blindar tu ventaja',
    planSub: 'Directo a tu email, ahora mismo. Sin compromiso.', planEmail: 'tu@email.com', planCta: 'Enviarme el plan',
    planSending: 'Enviando…', planDoneT: '¡Plan en camino! 📬', planDone: 'Revisa tu bandeja de entrada (y spam). Y si quieres ir más rápido:',
    planLegal: 'Al enviarlo aceptas los', terms: 'Términos', and: 'y la', privacy: 'Política de Privacidad',
    planErr: 'No pudimos registrar tu email. Inténtalo de nuevo.',
    refer: '¿Conoces a alguien con restaurante? Recomiéndanos y gana el 30% recurrente —',
    referCta: 'regístrate gratis como partner →',
  } : {
    eyebrow: 'New · Real live data', h: "Who's winning online: you or your competitor?",
    sub: "We scan your website and your direct competitor's live — same technology, same 30+ checks — and tell you exactly who dominates Google, ChatGPT & Gemini. No opinions: data.",
    you: 'yourrestaurant.com', rival: 'yourcompetitor.com', cta: 'Compare free', scanning: 'Scanning both websites live…',
    win: "You're ahead! But your competitor is one click away from catching up.", lose: 'Your competitor is beating you online — every day that passes, those are guests going to their tables, not yours.',
    tie: 'A technical tie — whoever acts first takes the lead.',
    yourSite: 'Your restaurant', rivalSite: 'Competitor', ctaFull: 'Get my full report & the plan to beat them',
    youLabel: 'Your website', rivalLabel: "Your competitor's website",
    err: "We couldn't scan one of the websites. Check the URLs.",
    planLoseT: '🥊 Get the FREE 90-day plan to beat them', planWinT: '🛡️ Get the FREE plan to protect your lead',
    planSub: 'Straight to your inbox, right now. No strings attached.', planEmail: 'you@email.com', planCta: 'Send me the plan',
    planSending: 'Sending…', planDoneT: 'Plan on its way! 📬', planDone: 'Check your inbox (and spam). Want to move faster?',
    planLegal: 'By submitting you accept the', terms: 'Terms', and: 'and the', privacy: 'Privacy Policy',
    planErr: "We couldn't register your email. Please try again.",
    refer: 'Know someone with a restaurant? Recommend us and earn 30% recurring —',
    referCta: 'register free as a partner →',
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!you.trim() || !rival.trim()) return;
    setBusy(true); setErr(''); setRes(null);
    try {
      const r = await fetch(`${API}/api/index-ai/compare`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: you.trim(), competitor_url: rival.trim(), lang, ref_code: getRef() || null }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(c.err); setBusy(false); return; }
      trackEvent('compare_completed', { verdict: (d?.you?.total ?? 0) > (d?.rival?.total ?? 0) ? 'win' : (d?.you?.total === d?.rival?.total ? 'tie' : 'lose') });
      setRes(d); setBusy(false);
    } catch { setErr(c.err); setBusy(false); }
  };

  const inputCls = 'flex-1 h-[52px] sm:h-12 rounded-xl border border-neutral-300 bg-white px-4 text-base sm:text-[15px] text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-[#F77F11] transition-colors min-w-0 w-full';
  const mineTotal = res?.you?.total ?? 0;
  const rivalTotal = res?.rival?.total ?? 0;
  const winning = mineTotal > rivalTotal;
  const tie = mineTotal === rivalTotal;

  const openRachelPlan = () => {
    window.dispatchEvent(new CustomEvent('kitxens:rachel-open', {
      detail: {
        view: 'compare_plan',
        context: {
          you: res?.you?.domain, rival: res?.rival?.domain,
          you_score: mineTotal, rival_score: rivalTotal,
          verdict: tie ? 'tie' : 'lose',
        },
      },
    }));
  };

  const sendPlan = async (e) => {
    e.preventDefault();
    if (!leadEmail.trim()) return;
    if (!planHuman) { setLeadErr(isEs ? 'Confirma que no eres un robot.' : "Please confirm you're not a robot."); return; }
    setLeadBusy(true); setLeadErr('');
    try {
      const r = await fetch(`${API}/api/lead`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Human-Token': planHuman },
        body: JSON.stringify({
          email: leadEmail.trim(), source: 'compare', locale: isEs ? 'es' : 'en',
          company: res?.you?.domain || '', interest: '90-day plan vs competitor',
          notes: `Compared ${res?.you?.domain} (${mineTotal}) vs ${res?.rival?.domain} (${rivalTotal}) — ${winning ? 'winning' : tie ? 'tie' : 'LOSING'}`,
          source_page: '/index-ai#compare',
          metadata: { you: res?.you?.domain, rival: res?.rival?.domain, you_score: mineTotal, rival_score: rivalTotal, verdict: winning ? 'win' : tie ? 'tie' : 'lose', ...(getRef() ? { ref: getRef() } : {}) },
        }),
      });
      if (!r.ok) { setLeadErr(c.planErr); setLeadBusy(false); return; }
      trackEvent('consultation_requested', { source: 'compare' });
      trackEvent('book_strategy_call', { source: 'compare' });
      setLeadSent(true); setLeadBusy(false);
    } catch { setLeadErr(c.planErr); setLeadBusy(false); }
  };

  return (
    <section id="compare" className="py-16 lg:py-24 bg-[#0F1116] scroll-mt-28" data-testid="competitor-compare">
      <div className="max-w-4xl mx-auto px-6">
        <p className="inline-flex items-center gap-2 rounded-full bg-[#F77F11]/15 text-[#FF9B4A] text-[11px] tracking-[0.2em] uppercase font-bold px-3.5 py-1.5">
          <Swords className="w-3.5 h-3.5" /> {c.eyebrow}
        </p>
        <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white tracking-tight leading-tight max-w-2xl">{c.h}</h2>
        <p className="mt-3 text-[15px] text-neutral-400 leading-relaxed max-w-2xl">{c.sub}</p>
        {comparesToday && (
          <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-[13px] font-semibold text-emerald-300" data-testid="compare-urgency">
            <span className="relative flex w-2 h-2 shrink-0">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            {isEs
              ? `${comparesToday} restaurantes se compararon hoy — la mayoría descubre que va perdiendo.`
              : `${comparesToday} restaurants ran a head-to-head today — most discover they're losing.`}
          </p>
        )}

        <form onSubmit={submit} className="mt-7 rounded-2xl bg-white/[0.04] border border-white/10 p-4 sm:p-5" data-testid="compare-form">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
            <div className="flex-1 w-full min-w-0">
              <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-400 mb-1.5">{c.youLabel}</label>
              <input value={you} onChange={(e) => setYou(e.target.value)} placeholder={c.you}
                inputMode="url" autoComplete="url" className={inputCls} data-testid="compare-you-input" />
            </div>
            <div className="flex items-center justify-center sm:pb-2.5 -my-1 sm:my-0">
              <span className="w-9 h-9 rounded-full bg-[#F77F11]/15 text-[#FF9B4A] text-[11px] font-bold flex items-center justify-center shrink-0 select-none" aria-hidden>VS</span>
            </div>
            <div className="flex-1 w-full min-w-0">
              <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-400 mb-1.5">{c.rivalLabel}</label>
              <input value={rival} onChange={(e) => setRival(e.target.value)} placeholder={c.rival}
                inputMode="url" autoComplete="url" className={inputCls} data-testid="compare-rival-input" />
            </div>
          </div>
          <button type="submit" disabled={busy}
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white h-12 px-8 rounded-xl text-[14.5px] font-bold transition-colors"
            data-testid="compare-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" />} {busy ? c.scanning : c.cta}
          </button>
        </form>
        {err && <p className="mt-3 text-[13px] text-red-400" data-testid="compare-error">{err}</p>}

        <AnimatePresence>
          {res && (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-8 rounded-3xl bg-white overflow-hidden shadow-2xl" data-testid="compare-results">
              <div className={`px-6 sm:px-8 py-4 flex items-start gap-3 ${winning ? 'bg-emerald-50 text-emerald-800' : tie ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>
                {winning ? <Trophy className="w-5 h-5 shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />}
                <p className="text-[14.5px] font-semibold leading-snug" data-testid="compare-verdict">{winning ? c.win : tie ? c.tie : c.lose}</p>
              </div>
              <div className="px-6 sm:px-8 py-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border-2 border-[#F77F11]/40 bg-orange-50/50 p-4 text-center">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-[#d8651a]">{c.yourSite}</p>
                    <p className="text-[12.5px] text-neutral-500 truncate">{res.you.domain}</p>
                    <p className="mt-1 text-4xl font-bold text-neutral-900 tabular-nums" data-testid="compare-you-score">{mineTotal}<span className="text-base text-neutral-400 font-medium">/100</span></p>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-center">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">{c.rivalSite}</p>
                    <p className="text-[12.5px] text-neutral-500 truncate">{res.rival.domain}</p>
                    <p className="mt-1 text-4xl font-bold text-neutral-700 tabular-nums" data-testid="compare-rival-score">{rivalTotal}<span className="text-base text-neutral-400 font-medium">/100</span></p>
                  </div>
                </div>
                <div className="mt-5 divide-y divide-neutral-100">
                  {Object.entries(res.you.categories).map(([k, v]) => (
                    <Bar key={k} label={isEs ? res.cat_labels[k].es : res.cat_labels[k].en}
                      mine={v} rival={res.rival.categories[k] ?? 0} max={res.cat_labels[k].max} />
                  ))}
                </div>
                {res.fix_now?.length > 0 && (
                  <div className="mt-6 rounded-2xl border-2 border-neutral-900 bg-neutral-950 text-white p-5 sm:p-6" data-testid="compare-fix-now">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF9B4A]">
                      ⚡ {isEs ? 'Qué hacer AHORA MISMO para corregirlo' : 'What to do RIGHT NOW to fix it'}
                    </p>
                    <div className="mt-3 space-y-3">
                      {res.fix_now.map((f, i) => (
                        <div key={f.category} className="flex items-start gap-3" data-testid={`compare-fix-${f.category}`}>
                          <span className="shrink-0 w-6 h-6 rounded-full bg-[#F77F11] text-white text-[12px] font-bold flex items-center justify-center">{i + 1}</span>
                          <div>
                            <p className="text-[13.5px] font-bold">{isEs ? f.label_es : f.label_en} <span className="text-[#FF9B4A] font-semibold">(−{f.gap} pts)</span></p>
                            <p className="text-[13px] text-neutral-300 leading-relaxed">{isEs ? f.fix_es : f.fix_en}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!winning && (
                  <button
                    type="button" onClick={openRachelPlan}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0F1116] hover:bg-neutral-800 text-white h-14 px-6 rounded-2xl text-[15px] font-bold transition-colors"
                    data-testid="compare-plan-90-btn"
                  >
                    🥊 {isEs ? 'Ver el plan de 90 días para adelantarles' : 'See the 90-day plan to beat them'} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <div className={`mt-6 rounded-2xl border-2 p-5 sm:p-6 ${winning ? 'border-neutral-200 bg-neutral-50' : 'border-[#F77F11] bg-orange-50/60'}`} data-testid="compare-lead-capture">
                  {leadSent ? (
                    <div className="text-center" data-testid="compare-lead-success">
                      <p className="text-lg font-bold text-neutral-900">{c.planDoneT}</p>
                      <p className="mt-1 text-[13.5px] text-neutral-600">{c.planDone}</p>
                      <Link to="/demo" className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-bold text-[#F77F11] hover:underline" data-testid="compare-lead-demo-link">
                        {isEs ? 'Agendar mi demo gratis' : 'Book my free demo'} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  ) : (
                    <>
                      <p className="text-[16.5px] font-bold text-neutral-900">{winning ? c.planWinT : c.planLoseT}</p>
                      <p className="mt-1 text-[13px] text-neutral-500">{c.planSub}</p>
                      <form onSubmit={sendPlan} className="mt-3 flex flex-col sm:flex-row gap-2.5">
                        <input type="email" value={leadEmail} onChange={(e) => { setLeadEmail(e.target.value); setLeadErr(''); }}
                          placeholder={c.planEmail} className={inputCls} data-testid="compare-lead-email" />
                        <button type="submit" disabled={leadBusy}
                          className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white h-12 px-6 rounded-xl text-[14.5px] font-bold transition-colors"
                          data-testid="compare-lead-submit">
                          {leadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {leadBusy ? c.planSending : c.planCta}
                        </button>
                      </form>
                      {leadErr && <p className="mt-2 text-[12.5px] text-red-500" data-testid="compare-lead-error">{leadErr}</p>}
                      <HumanCheck className="mt-3" onToken={setPlanHuman} />
                      <p className="mt-2.5 text-[12px] text-neutral-400">
                        {c.planLegal} <Link to="/terms" className="underline hover:text-neutral-600">{c.terms}</Link> {c.and} <Link to="/privacy" className="underline hover:text-neutral-600">{c.privacy}</Link>.
                      </p>
                    </>
                  )}
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a href="#scan" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-6 rounded-full text-[14.5px] font-bold transition-colors" data-testid="compare-cta-report">
                    {c.ctaFull} <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link to="/demo" className="inline-flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 hover:border-[#F77F11] hover:text-[#F77F11] h-12 px-6 rounded-full text-[14px] font-semibold transition-colors" data-testid="compare-cta-demo">
                    {isEs ? 'Agendar demo gratis' : 'Book a free demo'}
                  </Link>
                </div>
                <p className="mt-5 text-center text-[13px] text-neutral-500" data-testid="compare-refer-line">
                  {c.refer}{' '}
                  <Link to="/affiliates" className="text-[#F77F11] font-bold hover:underline">{c.referCta}</Link>
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
